import type { TFunction } from "i18next";
import { StatsMode } from "../../utils/models";
import {
	ACTIONS_COLUMN_WIDTH,
	COLUMN_FLEX,
	DAYS_COLUMN_MIN_WIDTH,
	FIGURE_COLUMN_MIN_WIDTH,
	HOURS_COLUMN_MIN_WIDTH,
	HOURS_WITH_MARK_MIN_WIDTH,
	JOB_COLUMN_MIN_WIDTH,
	LADDER_COLUMN_MIN_WIDTH,
	LOCATION_COLUMN_FIELD,
	LOCATION_COLUMN_MIN_WIDTH,
} from "./consts";
import type { LineFigure } from "./rows/LineFigureContent";

/**
 * A Locations grid column as it is laid out — its field, header, proportion and alignment — without what it draws.
 * The page's columns (`useLocationsGridColumns`) add their cells to these, and the loading skeleton
 * (`LocationsPageSkeleton`) draws its placeholder rows under the same headers, so the two can't disagree. Kept apart
 * from the cells so the skeleton, which loads before the page's own code, doesn't pull them in.
 */
export type LocationsColumnShape = {
	field: string;
	headerName: string;
	flex?: number;
	width?: number;
	minWidth?: number;
	sortable: boolean;
	hideHeaderSeparator: true;
	align?: "right";
	headerAlign?: "right";
};

/** A column of one figure per staffing line: Target, Required, or the ladder's rungs. */
export const figureColumnShape = (field: string, headerName: string, figure: LineFigure): LocationsColumnShape => ({
	field,
	headerName,
	flex: figure === "rung" ? COLUMN_FLEX.LADDER : COLUMN_FLEX.FIGURE,
	minWidth: figure === "rung" ? LADDER_COLUMN_MIN_WIDTH : FIGURE_COLUMN_MIN_WIDTH,
	sortable: false,
	hideHeaderSeparator: true,
	// Figures read as numbers do: right-aligned under a right-aligned header, as the design sets them.
	...(figure === "rung" ? {} : { align: "right" as const, headerAlign: "right" as const }),
});

// Sorted on the grid's own sort, which orders the bands (`rows/locationSort.ts`).
export const locationColumnShape = (headerName: string, statsMode: StatsMode): LocationsColumnShape => ({
	field: LOCATION_COLUMN_FIELD,
	headerName,
	flex: statsMode === StatsMode.Ratio ? COLUMN_FLEX.LOCATION_RATIO : COLUMN_FLEX.LOCATION,
	minWidth: LOCATION_COLUMN_MIN_WIDTH,
	sortable: true,
	hideHeaderSeparator: true,
});

// Sorted on the grid's own sort (`rows/locationSort.ts`), as is Hours.
export const jobColumnShape = (headerName: string): LocationsColumnShape => ({
	field: "jobLabels",
	headerName,
	flex: COLUMN_FLEX.JOB,
	minWidth: JOB_COLUMN_MIN_WIDTH,
	sortable: true,
	hideHeaderSeparator: true,
});

export const hoursColumnShape = (headerName: string, hasDuplicateMarks: boolean): LocationsColumnShape => ({
	field: "hours",
	headerName,
	flex: COLUMN_FLEX.HOURS,
	minWidth: hasDuplicateMarks ? HOURS_WITH_MARK_MIN_WIDTH : HOURS_COLUMN_MIN_WIDTH,
	sortable: true,
	hideHeaderSeparator: true,
});

export const daysColumnShape = (headerName: string): LocationsColumnShape => ({
	field: "lines",
	headerName,
	flex: COLUMN_FLEX.DAYS,
	minWidth: DAYS_COLUMN_MIN_WIDTH,
	sortable: false,
	hideHeaderSeparator: true,
});

export const actionsColumnShape = (): LocationsColumnShape => ({
	field: "__actions",
	headerName: "",
	width: ACTIONS_COLUMN_WIDTH,
	sortable: false,
	hideHeaderSeparator: true,
	align: "right",
	headerAlign: "right",
});

/** The figure columns' fields, headers and kinds: Target and Required (PPD), or the Ratio ladder. */
export const figureColumnsOf = (t: TFunction, statsMode: StatsMode): Array<{ field: string; headerName: string; figure: LineFigure }> =>
	statsMode === StatsMode.Ratio
		? [{ field: "__ladder", headerName: t("locations.grid.col.ratio"), figure: "rung" }]
		: [
				{ field: "__target", headerName: t("schedule.template.target"), figure: "target" },
				{ field: "__required", headerName: t("schedule.template.required"), figure: "required" },
			];

/** A department without job management staffs rows by ROLE, so the column must not claim "Job". */
export const jobColumnHeader = (t: TFunction, isJobManaged: boolean): string => (isJobManaged ? t("locations.grid.col.job") : t("locations.grid.col.roles"));

/** Every column's shape, in the grid's order: Location, Job, Hours, Days, the figures, the actions. */
export const locationsColumnShapes = (t: TFunction, isJobManaged: boolean, statsMode: StatsMode, hasDuplicateMarks: boolean): LocationsColumnShape[] => [
	locationColumnShape(t("location"), statsMode),
	jobColumnShape(jobColumnHeader(t, isJobManaged)),
	hoursColumnShape(t("locations.grid.col.hours"), hasDuplicateMarks),
	daysColumnShape(t("Days")),
	...figureColumnsOf(t, statsMode).map(({ field, headerName, figure }) => figureColumnShape(field, headerName, figure)),
	actionsColumnShape(),
];
